import { Component, DestroyRef, effect, HostListener, inject, model, OnInit } from '@angular/core';
import { CarouselModule, OwlOptions } from 'ngx-owl-carousel-o';
import { CarouselImagesStore } from '@data-access/manager/stores/carousel-manager.store';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';

import { Genre } from '@shared/interfacres/genre';

import { TrackData } from '@shared/interfacres/track';

import { debounceTime } from 'rxjs';
import { IconInput } from '@shared/components/icon-input/icon-input';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Category } from '@shared/interfacres/category';
import { TopTracks } from '@shared/components/top-tracks/top-tracks';
import { Router } from '@angular/router';
import { TracksList } from '@shared/components/tracks-list/tracks-list';
import { LibraryStore } from '@data-access/user/stores/library.store';
import { CarouselImage } from '@shared/interfacres/image';
import { saveToFs } from '@shared/helpers/files.helper';
import { MatDialog } from '@angular/material/dialog';
import { DemoUserHandler } from '@shared/services/demo-user-handler';

@Component({
  selector: 'app-library',
  templateUrl: './library.html',
  imports: [
    CarouselModule,
    MatProgressSpinner,
    MatIcon,
    FormsModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatInput,
    MatOption,
    ReactiveFormsModule,
    IconInput,
    TopTracks,
    TracksList
  ],
  styleUrl: './library.scss'
})
export class Library implements OnInit {

  isResized = false
  resizing = 0;
  readonly dialog = inject(MatDialog);

  @HostListener('window:resize', [])
  onResize() {
    this.isResized = true;
    clearTimeout(this.resizing);

    this.resizing = setTimeout(() => {
      this.isResized = false;
    }, 100)
  }

  dataSource: TrackData[] = [];
  tracksTotal = 0;

  readonly router = inject(Router);
  readonly store = inject(LibraryStore);
  readonly demoUserHandlerService = inject(DemoUserHandler);

  readonly destroyRef = inject(DestroyRef);

  carouselStore = inject(CarouselImagesStore);

  subGenre = model<Genre | null>(null);
  genre = model<Genre | null>(null);
  category = model<Category | null>(null);

  index = 0;

  searchValue = new FormControl<string>('');


  constructor() {
    this.carouselStore.getAll();

    effect(() => {
      this.dataSource = this.store.tracks();
      this.tracksTotal = this.store.total();
    });

    effect(() => {
      const value = this.subGenre()?.id || null;
      this.store.setSubGenreId(value);
      this.index = 0;
    });
    effect(() => {
      const value = this.genre()?.id || null;
      this.store.setGenreId(value);
      this.index = 0;
    });
    effect(() => {
      const value = this.category()?.id || null;
      this.store.setCategoryId(value);
      this.index = 0;
    });

  }

  async ngOnInit() {
    if (!this.demoUserHandlerService.initialMessageShown()) {
      await this.demoUserHandlerService.isUserValid(true);
    }

    this.store.getAttributesLists();

    this.searchValue.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(500),
      )
      .subscribe(input => {
        this.store.setName(input as string);
        this.index = 0;
      });


    this.store.setFilter('position', 'desc');
  }

  customOptions: OwlOptions = {
    // autoHeight: true,
    loop: true,
    mouseDrag: false,
    touchDrag: false,
    pullDrag: false,
    dots: true,
    center: true,
    autoWidth: false,
    // navSpeed: 500,
    autoplaySpeed: 500,
    autoplay: true,
    nav: false,
    // mergeFit: true,
    // stagePadding: -100,
    margin: 2,
    items: 1,
    autoplayTimeout: 8000,

    // navContainer: '.main-content .custom-nav',
    // responsive: {
    //   0: {
    //     items: 1
    //   },
    //   400: {
    //     items: 1
    //   },
    //   740: {
    //     items: 1
    //   },
    //   940: {
    //     items: 1
    //   },
    //   1240: {
    //     items: 1
    //   }
    // },
  }

  displayFn(item: {id: number; name: string}): string {
    return item?.name || '';
  }

  changePage(page: {pageIndex: number; pageSize: number}) {
    this.index = page.pageIndex;
    this.store.setPagination(page.pageIndex + 1, page.pageSize);
  }

  sortChange(event: {active: string; direction: string}) {
    this.store.setFilter(event.active, event.direction as 'asc' | 'desc');
  }

  openPanel(trigger: MatAutocompleteTrigger): void {
    trigger.openPanel();
  }

  slideClicked(item: CarouselImage) {
    if (item.url && item.url !== '') {
      window.open(item.url, "_self");
    }
  }

  async download(data: {id: number; title: string}) {
    const isUserValid = await this.demoUserHandlerService.isUserValid();
    if (!isUserValid) {
      return;
    }

    this.store.increaseTrackCounter(data.id);
    this.store.increaseTopTrackCounter(data.id);

    const directLink =  await this.store.getDownloadUrl(data.id);

    if(directLink) {
      await saveToFs(directLink, data.title);
    } else {
      this.store.decreaseTrackCounter(data.id);
      this.store.decreaseTopTrackCounter(data.id);
    }
  }

}
